import type { Project, ProjectLocation, UpdateProjectRequest } from '@p2b/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { useNavigate } from 'react-router';
import { ErrorNote } from '../../App.js';
import { api, projectRef } from '../../api.js';
import { LocationPicker } from '../../lazyMap.js';
import { label, useI18n } from '../../i18n.js';
import { Dialog } from '../org/widgets.js';

/** Refresh every list a project appears in. */
function invalidate(qc: ReturnType<typeof useQueryClient>, org: string) {
  void qc.invalidateQueries({ queryKey: ['projects'] });
  void qc.invalidateQueries({ queryKey: ['orgProjects', org] });
}

/**
 * A new project. From an organization's page the organization is given; from the home page the creator picks it.
 */
export function NewProjectDialog({
  org,
  orgs,
  onClose,
}: {
  org?: string;
  orgs?: { slug: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    org: org ?? '',
    name: '',
    slug: '',
    address: '',
    landNumber: '',
    municipality: '',
  });
  const create = useMutation({
    mutationFn: () =>
      api.createProject(form.org, {
        name: form.name,
        slug: form.slug,
        ...(form.address ? { address: form.address } : {}),
        ...(form.landNumber ? { landNumber: form.landNumber } : {}),
        ...(form.municipality ? { municipality: form.municipality } : {}),
      }),
    onSuccess: (p) => {
      invalidate(qc, p.org);
      navigate(`/projects/${p.org}/${p.slug}`);
    },
  });
  type Field = Exclude<keyof typeof form, 'org'>;
  const field = (k: Field, text: string, required = false, pattern?: string) => (
    <label className={k === 'name' || k === 'address' ? 'span-all' : undefined}>
      {text}
      <input
        required={required}
        pattern={pattern}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      />
    </label>
  );
  return (
    <Dialog
      title={t('newProject')}
      onClose={onClose}
      onSubmit={() => create.mutate()}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="primary" disabled={create.isPending}>
            {t('create')}
          </button>
        </>
      }
    >
      <div className="form-grid two">
        {!org && orgs && (
          <label className="span-all">
            {t('organization')}
            <select required value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })}>
              <option value="" disabled>
                —
              </option>
              {orgs.map((o) => (
                <option key={o.slug} value={o.slug}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {field('name', t('name'), true)}
        {field('slug', t('slug'), true, '[a-z0-9][a-z0-9-]*[a-z0-9]')}
        {field('landNumber', t('landNumber'))}
        {field('address', t('address'))}
        {field('municipality', t('municipality'))}
      </div>
      <ErrorNote error={create.error} />
    </Dialog>
  );
}

const fmt = (v: number) => v.toFixed(5).replace('.', ',');

/** Name, place and map location (managers; owner 2026-11-01). The slug, and so every address, stays. */
export function EditProjectDialog({ project: p, onClose }: { project: Project; onClose: () => void }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: p.name,
    address: p.address ?? '',
    municipality: p.municipality ?? '',
    landNumber: p.landNumber ?? '',
    description: p.description ?? '',
  });
  const [location, setLocation] = useState<ProjectLocation | null>(p.location ?? null);
  const save = useMutation({
    mutationFn: () => {
      const body: UpdateProjectRequest = {};
      if (form.name.trim() !== p.name) body.name = form.name;
      const text = (k: 'address' | 'municipality' | 'landNumber' | 'description') => {
        const v = form[k].trim() || null;
        if (v !== (p[k] ?? null)) body[k] = v;
      };
      text('address');
      text('municipality');
      text('landNumber');
      text('description');
      const before = p.location ?? null;
      if (JSON.stringify(location) !== JSON.stringify(before)) body.location = location;
      if (Object.keys(body).length === 0) return Promise.resolve(p);
      return api.updateProject(projectRef(p.org, p.slug), body);
    },
    onSuccess: (updated) => {
      qc.setQueryData(['project', projectRef(p.org, p.slug)], (old: Project | undefined) =>
        old ? { ...old, ...updated } : updated,
      );
      invalidate(qc, p.org);
      onClose();
    },
  });
  // Staðfangaskrá: by land number when the project has one, else by its address.
  const lookup = useMutation({
    mutationFn: () =>
      api.addresses(
        form.landNumber.trim() ? { landNumber: form.landNumber.trim() } : { q: form.address.trim() },
      ),
  });
  const field = (k: keyof typeof form, text: string, required = false) => (
    <label className={k === 'name' || k === 'description' ? 'span-all' : undefined}>
      {text}
      <input
        required={required}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      />
    </label>
  );
  return (
    <Dialog
      title={t('editProject')}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="primary" disabled={save.isPending}>
            {t('save')}
          </button>
        </>
      }
    >
      <div className="form-grid two">
        {field('name', t('name'), true)}
        {field('address', t('address'))}
        {field('municipality', t('municipality'))}
        {field('landNumber', t('landNumber'))}
        {field('description', t('meta_description'))}
        <div className="span-all">
          <div className="loc-head">
            <span className="field-label">{t('location')}</span>
            <button
              type="button"
              className="quiet sm"
              disabled={lookup.isPending || !(form.landNumber.trim() || form.address.trim())}
              onClick={() => lookup.mutate()}
            >
              {lookup.isPending ? t('loading') : t('findInRegistry')}
            </button>
          </div>
          {lookup.data && (
            <div className="registry-hits" role="list" aria-label={t('registry')}>
              {lookup.data.length === 0 && <span className="muted">{t('registryNone')}</span>}
              {lookup.data.slice(0, 8).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="listitem"
                  className={`chip${location?.source === 'registry' && location.lat === a.lat && location.lon === a.lon ? ' on' : ''}`}
                  onClick={() => {
                    setLocation({ lat: a.lat, lon: a.lon, source: 'registry' });
                    setForm((f) => ({
                      ...f,
                      landNumber: f.landNumber || a.landNumber || '',
                      address: f.address || a.address,
                    }));
                  }}
                >
                  {a.address}
                  {a.postcode ? `, ${a.postcode}` : ''}
                  <span className="faint"> · {a.landNumber ?? '—'}</span>
                </button>
              ))}
            </div>
          )}
          <Suspense fallback={<div className="mapbox picker" />}>
            <LocationPicker value={location} onChange={(v) => setLocation({ ...v, source: 'map' })} />
          </Suspense>
          <div className="loc-line">
            {location ? (
              <>
                <span className={`tag${location.source === 'map' ? '' : ' ok'}`}>
                  {label(t, 'location', location.source)}
                </span>
                <span className="num">
                  {fmt(location.lat)}° N, {fmt(Math.abs(location.lon))}°{' '}
                  {location.lon < 0 ? (lang === 'is' ? 'V' : 'W') : lang === 'is' ? 'A' : 'E'}
                </span>
                <button type="button" className="quiet sm" onClick={() => setLocation(null)}>
                  {t('remove')}
                </button>
              </>
            ) : (
              <span className="muted">{t('locationHint')}</span>
            )}
          </div>
        </div>
      </div>
      <ErrorNote error={save.error} />
    </Dialog>
  );
}
